<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <div class="page-title">
          다단센서 관리
          <span class="count-text">총 {{ resolvedTotalCount }} 건</span>
        </div>
        <div class="page-subtitle">
          RS485 기반 다단센서 후보 로거를 조회하고 sensor_group 을 생성/수정합니다.
        </div>
      </div>
    </div>

    <div class="content-wrapper">
      <div class="left-section">
        <div class="filter-section">
          <div class="filter-grid">
            <div class="filter-item">
              <label class="filter-label">라인번호</label>
              <input
                v-model="searchForm.lineNumber"
                class="control"
                placeholder="라인번호 입력"
                @keyup.enter="fetchCandidateLoggers"
              />
            </div>

            <div class="filter-item">
              <label class="filter-label">로거명</label>
              <input
                v-model="searchForm.loggerName"
                class="control"
                placeholder="로거명 입력"
                @keyup.enter="fetchCandidateLoggers"
              />
            </div>

            <div class="filter-item">
              <label class="filter-label">현장명</label>
              <input
                v-model="searchForm.siteName"
                class="control"
                placeholder="현장명 입력"
                @keyup.enter="fetchCandidateLoggers"
              />
            </div>

            <div class="filter-item">
              <label class="filter-label">다단센서 유형</label>
              <select v-model="searchForm.multiSensorType" class="control" @change="onChangeMultiSensorType">
                <option value="">전체</option>
                <option value="TS">지중경사계(TS)</option>
                <option value="R">레일변위계(R)</option>
                <option value="TN">내공변위계(TN)</option>
              </select>
            </div>

            <div class="filter-actions">
              <button class="btn primary" @click="fetchCandidateLoggers">검색</button>
              <button class="btn secondary" @click="resetFilter">초기화</button>
            </div>
          </div>
        </div>

        <div class="table-section">
          <table v-if="tableRows.length" class="data-table logger-table">
            <thead>
              <tr>
                <th class="col-type">유형</th>
                <th class="col-logger">로거명</th>
                <th class="col-line">라인번호</th>
                <th class="col-site">현장명</th>
                <th class="col-node">노드수</th>
                <th class="col-status">상태</th>
                <th class="col-actions">작업</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="row in tableRows"
                :key="`${row.sensorLoggerId}_${row.multiSensorType || searchForm.multiSensorType || 'ALL'}`"
                :class="{ 
                  'row-selected':
                    selectedLoggerId === row.sensorLoggerId &&
                    selectedMultiSensorType === row.multiSensorType
                }"
                class="clickable-row"
                @click="onClickRow(row)"
              >
                <td class="col-type-cell">
                  {{ row.multiSensorTypeName || row.multiSensorType || '-' }}
                </td>

                <td class="col-logger-cell" :title="row.loggerName || '-'">
                  {{ row.loggerName || '-' }}
                </td>

                <td class="col-line-cell">
                  {{ row.lineNumber || '-' }}
                </td>

                <td class="col-site-cell" :title="row.siteName || '-'">
                  {{ row.siteName || '-' }}
                </td>

                <td class="col-node-cell">
                  {{ row.nodeCount || 0 }}
                </td>

                <td class="col-status-cell">
                  <span class="badge" :class="resolveBadgeClass(row.status)">
                    {{ row.status || '-' }}
                  </span>
                  <div v-if="!row.siteId" class="mini-warning-text">
                    현장 미설정
                  </div>
                </td>
                <td class="td-actions" @click.stop>
                  <div class="row-actions">

                    <!-- 생성 전 -->
                    <button
                      v-if="!row.groupCreated && row.status !== '생성완료'"
                      class="btn xs primary"
                      @click="onClickSelect(row)"
                      :disabled="row.nodeCount < 2 || !row.siteId"
                      :title="resolveCreateButtonTitle(row)"
                    >
                      선택
                    </button>

                    <!-- 생성 후 -->
                    <button
                      v-else
                      class="btn xs secondary"
                      @click="onClickEditGroup(row)"
                      :disabled="!row.siteId"
                    >
                      수정
                    </button>

                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-else class="table-empty">
            {{ loading ? '조회 중입니다...' : '표시할 다단센서 후보 로거가 없습니다' }}
          </div>
        </div>
      </div>
    </div>

    <!-- 중앙 상세 모달 -->
    <div
      v-if="isDetailModalOpen"
      class="multi-modal-backdrop"
      @click.self="closeDetailModal"
    >
      <div class="multi-modal-panel">
        <div class="multi-modal-header">
          <div>
            <div class="multi-modal-title">후보 로거 상세</div>
            <div v-if="isEditMode" class="mode-text edit">
              수정 모드 / sensor_group_id: {{ selectedGroupId }}
            </div>
            <div v-else class="mode-text create">
              생성 모드 / {{ resolvedTypeLabel }}
            </div>
          </div>

          <div class="multi-modal-actions">
            <button class="btn sm secondary" @click="reloadCurrentDetail">
              새로고침
            </button>
            <button class="modal-close-btn" type="button" @click="closeDetailModal">
              ×
            </button>
          </div>
        </div>

        <div v-if="selectedLoggerDetail" class="multi-modal-body">
          <div class="modal-two-column">
            <div class="modal-section">
              <div class="section-title">로거 정보</div>

              <div class="form-grid mt-16">
                <div class="form-row">
                  <label class="form-label">로거명</label>
                  <div class="form-field">
                    <input class="control" :value="selectedLoggerDetail.loggerName || '-'" readonly />
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">라인번호</label>
                  <div class="form-field">
                    <input class="control" :value="selectedLoggerDetail.lineNumber || '-'" readonly />
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">현장명</label>
                  <div class="form-field">
                    <input class="control" :value="selectedLoggerDetail.siteName || '-'" readonly />
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">다단센서 유형</label>
                  <div class="form-field">
                    <input class="control" :value="selectedLoggerDetail.multiSensorTypeName || resolvedTypeLabel" readonly />
                  </div>
                </div>
              </div>

              <div v-if="!selectedLoggerDetail?.siteId" class="warning-box mt-16">
                ⚠️ 이 로거는 현장이 연결되어 있지 않습니다.<br />
                <b>로거/센서 관리</b>에서 현장을 먼저 설정한 뒤 그룹을 생성해주세요.
              </div>
            </div>

            <div class="modal-section">
              <div class="section-title">프로파일 / 기준값 설정</div>

              <div class="form-grid mt-16">
                <template v-if="isTsLike">
                  <div class="form-row">
                    <label class="form-label">시작 depth</label>
                    <div class="form-field">
                      <input v-model.number="groupForm.startDepth" type="number" step="0.1" class="control" />
                    </div>
                  </div>

                  <div class="form-row">
                    <label class="form-label">depth 간격</label>
                    <div class="form-field">
                      <input v-model.number="groupForm.depthInterval" type="number" step="0.1" class="control" />
                    </div>
                  </div>

                  <div class="form-row">
                    <label class="form-label">프로파일 축</label>
                    <div class="form-field">
                      <select v-model="groupForm.profileAxis" class="control">
                        <option value="X">X 축 사용</option>
                        <option value="Y">Y 축 사용</option>
                      </select>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="form-row">
                    <label class="form-label">프로파일 평면</label>
                    <div class="form-field">
                      <input class="control" value="XZ" readonly />
                    </div>
                  </div>

                  <div class="form-row">
                    <label class="form-label">노드 기준</label>
                    <div class="form-field">
                      <input class="control" value="Dev 순서 / TN_ANGLE" readonly />
                    </div>
                  </div>
                </template>

                <div class="form-row">
                  <label class="form-label">차트 최소값</label>
                  <div class="form-field">
                    <input v-model.number="groupForm.chartMin" type="number" class="control" />
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">차트 최대값</label>
                  <div class="form-field">
                    <input v-model.number="groupForm.chartMax" type="number" class="control" />
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">1차 기준</label>
                  <div class="form-field">
                    <input v-model.number="groupForm.threshold1" type="number" class="control" />
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">2차 기준</label>
                  <div class="form-field">
                    <input v-model.number="groupForm.threshold2" type="number" class="control" />
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">3차 기준</label>
                  <div class="form-field">
                    <input v-model.number="groupForm.threshold3" type="number" class="control" />
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">각도 스케일</label>
                  <div class="form-field">
                    <input v-model.number="groupForm.angleScale" type="number" step="0.001" class="control" />
                  </div>
                </div>

                <div class="form-row">
                  <label class="form-label">구간 길이(mm)</label>
                  <div class="form-field">
                    <input v-model.number="groupForm.segmentLengthMm" type="number" class="control" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-section mt-20">
            <div class="section-title">
              {{ isEditMode ? '그룹 수정 설정' : '그룹 생성 설정' }}
            </div>

            <div class="form-grid group-form-grid mt-16">
              <div class="form-row">
                <label class="form-label required">그룹명</label>
                <div class="form-field">
                  <input
                    v-model="groupForm.groupName"
                    class="control"
                    :placeholder="isTN
                      ? '예: 내공변위계-1222364957'
                      : (isR ? '예: 레일변위계-9999999997' : '예: 지중경사계-1222364957')"
                  />
                </div>
              </div>

              <div class="form-row">
                <label class="form-label">설치위치 설명</label>
                <div class="form-field">
                  <input
                    v-model="groupForm.locationDesc"
                    class="control"
                    placeholder="설치 위치 설명"
                  />
                </div>
              </div>

              <div class="form-row align-start preview-form-row">
                <label class="form-label">
                  {{ isTN ? '노드 미리보기' : '자동 depth 미리보기' }}
                </label>
                <div class="form-field">
                  <div class="preview-box">
                    <div
                      v-for="node in depthPreviewRows"
                      :key="node.nodeKey"
                      class="preview-row"
                    >
                      <span class="preview-col">{{ node.devKeyName || '-' }}</span>
                      <span class="preview-col">{{ node.address || '-' }}</span>
                      <span class="preview-col">
                        {{ isTN ? `node ${node.depth}` : `depth ${node.depth}` }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-row action-row">
                <label class="form-label"></label>
                <div class="form-field action-button-wrap">
                  <button
                    v-if="isEditMode"
                    class="btn primary"
                    @click="updateSensorGroup"
                    :disabled="isUpdateDisabled"
                  >
                    {{ updating ? '수정 중...' : 'sensor_group 수정' }}
                  </button>

                  <button
                    v-else
                    class="btn primary"
                    @click="createSensorGroup"
                    :disabled="isCreateDisabled"
                    :title="createDisabledTitle"
                  >
                    {{ creating ? '생성 중...' : 'sensor_group 생성' }}
                  </button>

                  <button class="btn ghost" @click="closeDetailModal">
                    닫기
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-section mt-20">
            <div class="section-title">노드 후보 목록</div>

            <div v-if="depthPreviewRows.length" class="node-table-scroll mt-12">
              <table class="data-table node-table">
                <thead>
                  <tr>
                    <th>nodeKey</th>
                    <th>Dev</th>
                    <th>Address</th>
                    <th v-if="isTsLike">X sensorId</th>
                    <th v-if="isTsLike">Y sensorId</th>
                    <th v-if="isTN">value sensorId</th>
                    <th v-if="isTN">measureKey</th>
                    <th>{{ isTN ? 'node' : '자동 depth' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in depthPreviewRows" :key="row.nodeKey">
                    <td>{{ row.nodeKey }}</td>
                    <td>{{ row.devKeyName }}</td>
                    <td>{{ row.address || '-' }}</td>
                    <td v-if="isTsLike">{{ row.xSensorId || '-' }}</td>
                    <td v-if="isTsLike">{{ row.ySensorId || '-' }}</td>
                    <td v-if="isTN">{{ row.valueSensorId || '-' }}</td>
                    <td v-if="isTN">{{ row.measureKey || 'TN_ANGLE' }}</td>
                    <td>{{ row.depth }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="empty-text">
              표시할 노드 후보가 없습니다
            </div>
          </div>
        </div>

        <div v-else class="modal-empty">
          상세 정보를 불러오지 못했습니다.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'

export default {
  name: 'MultiSensorManage',

  data() {
    return {
      tableRows: [],
      totalCount: 0,
      loading: false,
      creating: false,
      updating: false,

      isDetailModalOpen: false,

      searchForm: {
        lineNumber: '',
        loggerName: '',
        siteName: '',
        multiSensorType: '',
      },

      selectedLoggerId: null,
      selectedMultiSensorType: '',
      selectedLoggerDetail: null,

      selectedGroupId: null,
      selectedGroupDetail: null,

      groupForm: this.getDefaultGroupForm('TS'),
    }
  },

  computed: {
    resolvedTotalCount() {
      return this.totalCount || this.tableRows.length
    },

    isEditMode() {
      return !!this.selectedGroupId
    },

    resolvedMultiSensorType() {
      return this.selectedLoggerDetail?.multiSensorType || this.selectedMultiSensorType || this.searchForm.multiSensorType || 'TS'
    },

    isTN() {
      return this.resolvedMultiSensorType === 'TN'
    },

    isR() {
      return this.resolvedMultiSensorType === 'R'
    },

    isTS() {
      return this.resolvedMultiSensorType === 'TS'
    },

    isTsLike() {
      return this.isTS || this.isR
    },

    resolvedTypeLabel() {
      if (this.isTN) return '내공변위계(TN)'
      if (this.isR) return '레일변위계(R)'
      return '지중경사계(TS)'
    },

    candidateNodes() {
      return Array.isArray(this.selectedLoggerDetail?.nodes)
        ? this.selectedLoggerDetail.nodes
        : []
    },

    depthPreviewRows() {
      if (!this.candidateNodes.length) return []

      const startDepth = Number(this.groupForm.startDepth ?? 0)
      const depthInterval = Number(this.groupForm.depthInterval ?? 0.5)

      return this.candidateNodes.map((node, index) => {
        const depth = this.isTN
          ? index
          : Number((startDepth + (depthInterval * index)).toFixed(4))

        return {
          ...node,
          depth,
        }
      })
    },

    isCreateDisabled() {
      return (
        this.creating ||
        !this.selectedLoggerId ||
        !this.depthPreviewRows.length ||
        !this.selectedLoggerDetail?.siteId ||
        !!this.selectedLoggerDetail?.groupCreated ||
        !this.groupForm.groupName?.trim()
      )
    },

    isUpdateDisabled() {
      return (
        this.updating ||
        !this.selectedGroupId ||
        !this.selectedLoggerId ||
        !this.depthPreviewRows.length ||
        !this.groupForm.groupName?.trim()
      )
    },

    createDisabledTitle() {
      if (!this.selectedLoggerId) return '먼저 로거를 선택해주세요.'
      if (!this.depthPreviewRows.length) return '생성 가능한 노드가 없습니다.'
      if (!this.selectedLoggerDetail?.siteId) return '현장을 먼저 설정해야 합니다.'
      if (this.selectedLoggerDetail?.groupCreated) return '이미 생성된 sensor_group 입니다.'
      if (!this.groupForm.groupName?.trim()) return '그룹명을 입력해주세요.'
      return ''
    },
  },

  async mounted() {
    await this.fetchCandidateLoggers()
  },

  methods: {

    async onClickRow(row) {
      if (!row?.siteId) {
        alert('현장을 먼저 설정해야 합니다.')
        return
      }

      // 생성완료 → 수정 버튼과 동일
      if (row.groupCreated || row.status === '생성완료') {
        await this.onClickEditGroup(row)
        return
      }

      // 생성대기 → 선택 버튼과 동일
      await this.onClickSelect(row)
    },

    getDefaultGroupForm(type = 'TS') {
      const isTN = type === 'TN'
      const isR = type === 'R'

      return {
        groupName: '',
        locationDesc: '',

        startDepth: 0.0,
        depthInterval: isR ? 1.0 : 0.5,
        profileAxis: isTN ? 'XZ' : (isR ? 'Y' : 'X'),

        chartMin: isR ? -10 : -100,
        chartMax: isR ? 10 : 100,

        threshold1: isR ? 4 : 22,
        threshold2: isR ? 7 : 29,
        threshold3: isR ? 10 : 36,

        angleScale: isTN ? 1.0 : 0.01,
        segmentLengthMm: 1000,
      }
    },

    openDetailModal() {
      this.isDetailModalOpen = true
    },

    closeDetailModal() {
      this.isDetailModalOpen = false
      this.exitEditMode()
    },

    onChangeMultiSensorType() {
      this.closeDetailModal()
      this.exitEditMode()
      this.selectedLoggerId = null
      this.selectedLoggerDetail = null
      this.selectedMultiSensorType = this.searchForm.multiSensorType || ''

      if (this.selectedMultiSensorType) {
        this.groupForm = this.getDefaultGroupForm(this.selectedMultiSensorType)
      } else {
        this.groupForm = this.getDefaultGroupForm('TS')
      }
    },

    resolveBadgeClass(status) {
      if (status === '생성완료') return 'success'
      if (status === '구조이상') return 'danger'
      return 'warning'
    },

    resolveCreateButtonTitle(row) {
      if (!row.siteId) return '현장을 먼저 설정해야 합니다.'
      if (row.groupCreated || row.status === '생성완료') return '이미 생성된 sensor_group 입니다.'
      if (row.nodeCount < 2) return '노드 수가 부족합니다.'
      return ''
    },

    resolveRowType(row) {
      return row?.multiSensorType || this.searchForm.multiSensorType || ''
    },

    resetFilter() {
      this.closeDetailModal()

      this.searchForm = {
        lineNumber: '',
        loggerName: '',
        siteName: '',
        multiSensorType: '',
      }

      this.exitEditMode()
      this.selectedLoggerId = null
      this.selectedMultiSensorType = ''
      this.selectedLoggerDetail = null
      this.groupForm = this.getDefaultGroupForm('TS')

      this.fetchCandidateLoggers()
    },

    async fetchCandidateLoggers() {
      this.loading = true

      try {
        const response = await api.get('/app/sensorGroup/candidateLoggers', {
          params: {
            lineNumber: this.searchForm.lineNumber || undefined,
            loggerName: this.searchForm.loggerName || undefined,
            siteName: this.searchForm.siteName || undefined,
            multiSensorType: this.searchForm.multiSensorType || undefined,
          }
        })

        const result = response?.data?.result || []
        this.tableRows = Array.isArray(result) ? result : []
        this.totalCount = this.tableRows.length
      } catch (e) {
        console.error('다단센서 후보 로거 조회 실패', e)
        this.tableRows = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },

    async fetchCandidateDetail(loggerId, multiSensorType) {
      if (!loggerId) return

      const targetType =
        multiSensorType ||
        this.selectedMultiSensorType ||
        this.searchForm.multiSensorType ||
        null

      if (!targetType) {
        console.warn('다단센서 유형이 선택되지 않았습니다.')
        return
      }

      try {
        const response = await api.get('/app/sensorGroup/candidateDetail', {
          params: {
            sensorLoggerId: loggerId,
            multiSensorType: targetType
          }
        })

        this.selectedLoggerDetail = response?.data?.result || null
        this.selectedMultiSensorType = targetType

        if (this.selectedLoggerDetail && !this.isEditMode) {
          this.groupForm = {
            ...this.getDefaultGroupForm(targetType),
            groupName:
              this.selectedLoggerDetail.defaultGroupName ||
              (
                targetType === 'TN'
                  ? `내공변위계-${loggerId}`
                  : targetType === 'R'
                    ? `레일변위계-${loggerId}`
                    : `지중경사계-${loggerId}`
              ),
          }
        }

        const selectedRow = this.tableRows.find(row =>
          Number(row.sensorLoggerId) === Number(loggerId) &&
          row.multiSensorType === targetType
        )

        if (selectedRow && this.selectedLoggerDetail) {
          this.selectedLoggerDetail.groupCreated = !!selectedRow.groupCreated
        }
      } catch (e) {
        console.error('다단센서 후보 상세 조회 실패', e)
        this.selectedLoggerDetail = null
      }
    },

    async fetchGroupDetailById(groupId) {
      if (!groupId) return null

      const response = await api.get('/app/sensorGroup/detail', {
        params: { id: groupId }
      })

      return response?.data?.result || null
    },

    async findExistingGroupByLogger(row) {
      if (!row?.siteId || !row?.sensorLoggerId) return null

      const response = await api.get('/app/sensorGroup/list', {
        params: { siteId: row.siteId }
      })

      const list = response?.data?.result || []
      if (!Array.isArray(list)) return null

      const rowType = this.resolveRowType(row)

      return list.find(group => {
        const sameLogger = Number(group.sensorLoggerId) === Number(row.sensorLoggerId)
        if (!sameLogger) return false

        if (rowType && group.multiSensorType) {
          return group.multiSensorType === rowType
        }

        return true
      }) || null
    },

    async loadExistingGroup(row) {
      const groupSummary = await this.findExistingGroupByLogger(row)

      if (!groupSummary?.id) {
        alert('생성된 sensor_group 정보를 찾지 못했습니다.')
        return
      }

      const detail = await this.fetchGroupDetailById(groupSummary.id)

      if (!detail?.id) {
        alert('sensor_group 상세 정보를 조회하지 못했습니다.')
        return
      }

      this.selectedGroupId = detail.id
      this.selectedGroupDetail = detail
      this.applyGroupDetailToForm(detail)
    },

    applyGroupDetailToForm(detail) {
      const configMap = this.toConfigMap(detail?.configs || [])
      const defaultForm = this.getDefaultGroupForm(this.selectedMultiSensorType)

      this.groupForm = {
        ...defaultForm,
        groupName: detail?.groupName || '',
        locationDesc: detail?.locationDesc || '',

        startDepth: this.toNumber(configMap.start_depth, defaultForm.startDepth),
        depthInterval: this.toNumber(configMap.depth_interval, defaultForm.depthInterval),
        profileAxis: configMap.profile_axis || defaultForm.profileAxis,

        chartMin: this.toNumber(configMap.chart_min, defaultForm.chartMin),
        chartMax: this.toNumber(configMap.chart_max, defaultForm.chartMax),

        threshold1: this.toNumber(configMap.threshold_1_main, defaultForm.threshold1),
        threshold2: this.toNumber(configMap.threshold_2_main, defaultForm.threshold2),
        threshold3: this.toNumber(configMap.threshold_3_main, defaultForm.threshold3),

        angleScale: this.toNumber(configMap.angle_scale, defaultForm.angleScale),
        segmentLengthMm: this.toNumber(configMap.segment_length_mm, defaultForm.segmentLengthMm),
      }
    },

    toConfigMap(configs) {
      const map = {}

      ;(configs || []).forEach(config => {
        if (!config?.fieldKey) return
        map[config.fieldKey] = config.fieldValue
      })

      return map
    },

    toNumber(value, defaultValue) {
      const num = Number(value)
      return Number.isFinite(num) ? num : defaultValue
    },

    async reloadCurrentDetail() {
      if (!this.selectedLoggerId || !this.selectedMultiSensorType) return

      await this.fetchCandidateDetail(this.selectedLoggerId, this.selectedMultiSensorType)

      if (this.isEditMode && this.selectedGroupId) {
        const detail = await this.fetchGroupDetailById(this.selectedGroupId)
        this.selectedGroupDetail = detail
        this.applyGroupDetailToForm(detail)
      }
    },

    async onClickSelect(row) {
      this.exitEditMode()

      const rowType = this.resolveRowType(row)

      if (!rowType) {
        alert('다단센서 유형을 확인할 수 없습니다.')
        return
      }

      this.selectedLoggerId = row.sensorLoggerId
      this.selectedMultiSensorType = rowType
      this.searchForm.multiSensorType = rowType

      await this.fetchCandidateDetail(row.sensorLoggerId, rowType)

      if (this.selectedLoggerDetail) {
        this.openDetailModal()
      }
    },

    async onClickEditGroup(row) {
      if (!row.siteId) {
        alert('현장을 먼저 설정해야 합니다.')
        return
      }

      const rowType = this.resolveRowType(row)

      if (!rowType) {
        alert('다단센서 유형을 확인할 수 없습니다.')
        return
      }

      this.selectedLoggerId = row.sensorLoggerId
      this.selectedMultiSensorType = rowType
      this.searchForm.multiSensorType = rowType
      this.exitEditMode()

      await this.fetchCandidateDetail(row.sensorLoggerId, rowType)
      await this.loadExistingGroup(row)

      if (this.selectedLoggerDetail) {
        this.openDetailModal()
      }
    },

    exitEditMode() {
      this.selectedGroupId = null
      this.selectedGroupDetail = null
    },

    buildGroupConfigs() {
      const threshold1 = Number(this.groupForm.threshold1 ?? 22)
      const threshold2 = Number(this.groupForm.threshold2 ?? 29)
      const threshold3 = Number(this.groupForm.threshold3 ?? 36)

      const commonConfigs = [
        { fieldKey: 'chart_min', fieldValue: String(this.groupForm.chartMin ?? -100) },
        { fieldKey: 'chart_max', fieldValue: String(this.groupForm.chartMax ?? 100) },

        { fieldKey: 'threshold_1_main', fieldValue: String(threshold1) },
        { fieldKey: 'threshold_1_sub', fieldValue: String(-Math.abs(threshold1)) },
        { fieldKey: 'threshold_2_main', fieldValue: String(threshold2) },
        { fieldKey: 'threshold_2_sub', fieldValue: String(-Math.abs(threshold2)) },
        { fieldKey: 'threshold_3_main', fieldValue: String(threshold3) },
        { fieldKey: 'threshold_3_sub', fieldValue: String(-Math.abs(threshold3)) },

        { fieldKey: 'angle_scale', fieldValue: String(this.groupForm.angleScale ?? 0.01) },
        { fieldKey: 'segment_length_mm', fieldValue: String(this.groupForm.segmentLengthMm ?? 1000) },
      ]

      if (this.isTN) {
        return [
          { fieldKey: 'profile_axis', fieldValue: 'XZ' },
          { fieldKey: 'node_axis', fieldValue: 'ANGLE' },
          { fieldKey: 'profile_plane', fieldValue: 'XZ' },
          ...commonConfigs,
        ]
      }

      return [
        { fieldKey: 'start_depth', fieldValue: String(this.groupForm.startDepth ?? 0) },
        { fieldKey: 'depth_interval', fieldValue: String(this.groupForm.depthInterval ?? 0.5) },
        { fieldKey: 'profile_axis', fieldValue: this.groupForm.profileAxis || 'X' },
        ...commonConfigs,
      ]
    },

    buildGroupItems() {
      return this.depthPreviewRows.flatMap(node => {
        const rows = []

        if (this.isTN) {
          const sensorId = node.valueSensorId

          if (sensorId) {
            rows.push({
              sensorId,
              depth: node.depth,
              sortOrder: node.nodeNo,
              axisType: 'ANGLE',
            })
          }

          return rows
        }

        if (node.xSensorId) {
          rows.push({
            sensorId: node.xSensorId,
            depth: node.depth,
            sortOrder: (node.nodeNo * 2) + 1,
            axisType: 'X',
          })
        }

        if (node.ySensorId) {
          rows.push({
            sensorId: node.ySensorId,
            depth: node.depth,
            sortOrder: (node.nodeNo * 2) + 2,
            axisType: 'Y',
          })
        }

        return rows
      })
    },

    async createSensorGroup() {
      if (!this.selectedLoggerDetail?.siteId) {
        alert('현장이 연결되지 않은 로거입니다. 현장 연결 후 생성해주세요.')
        return
      }

      if (this.selectedLoggerDetail?.groupCreated) {
        alert('이미 생성된 sensor_group 입니다.')
        return
      }

      if (!this.selectedLoggerId) {
        alert('먼저 로거를 선택해주세요.')
        return
      }

      if (!this.groupForm.groupName?.trim()) {
        alert('그룹명을 입력해주세요.')
        return
      }

      if (!this.depthPreviewRows.length) {
        alert('생성 가능한 노드가 없습니다.')
        return
      }

      this.creating = true

      try {
        const requestBody = {
          sensorLoggerId: this.selectedLoggerId,
          multiSensorType: this.resolvedMultiSensorType,
          groupName: this.groupForm.groupName.trim(),
          locationDesc: this.groupForm.locationDesc?.trim() || '',
          useYn: true,
          configs: this.buildGroupConfigs(),
          items: this.buildGroupItems(),
        }

        const response = await api.post('/app/sensorGroup/createFromLogger', requestBody)

        alert(response?.data?.message || 'sensor_group 생성이 완료되었습니다.')

        this.closeDetailModal()

        await this.fetchCandidateLoggers()
        await this.fetchCandidateDetail(this.selectedLoggerId, this.selectedMultiSensorType)
      } catch (e) {
        console.error('sensor_group 생성 실패', e)

        const status = e?.response?.status
        const message = e?.response?.data?.message

        if (status === 409) {
          alert(message || '이미 생성된 sensor_group 입니다.')
          await this.fetchCandidateLoggers()
          await this.fetchCandidateDetail(this.selectedLoggerId, this.selectedMultiSensorType)
          return
        }

        alert(message || 'sensor_group 생성에 실패했습니다.')
      } finally {
        this.creating = false
      }
    },

    async updateSensorGroup() {
      if (!this.selectedGroupId) {
        alert('수정할 sensor_group 이 선택되지 않았습니다.')
        return
      }

      if (!this.groupForm.groupName?.trim()) {
        alert('그룹명을 입력해주세요.')
        return
      }

      if (!this.depthPreviewRows.length) {
        alert('수정 가능한 노드가 없습니다.')
        return
      }

      this.updating = true

      try {
        const requestBody = {
          id: this.selectedGroupId,
          siteId: this.selectedLoggerDetail?.siteId || null,
          sensorLoggerId: this.selectedLoggerId,
          sensorTypeId: this.selectedGroupDetail?.sensorTypeId || null,
          groupName: this.groupForm.groupName.trim(),
          locationDesc: this.groupForm.locationDesc?.trim() || '',
          useYn: true,
          deleted: false,
          configs: this.buildGroupConfigs(),
          items: this.buildGroupItems(),
        }

        const response = await api.post('/app/sensorGroup/update', requestBody)

        alert(response?.data?.message || 'sensor_group 수정이 완료되었습니다.')

        this.closeDetailModal()

        await this.fetchCandidateLoggers()
        await this.fetchCandidateDetail(this.selectedLoggerId, this.selectedMultiSensorType)

        const detail = await this.fetchGroupDetailById(this.selectedGroupId)
        this.selectedGroupDetail = detail
        this.applyGroupDetailToForm(detail)
      } catch (e) {
        console.error('sensor_group 수정 실패', e)
        const message = e?.response?.data?.message
        alert(message || 'sensor_group 수정에 실패했습니다.')
      } finally {
        this.updating = false
      }
    },
  }
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-table.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-input.css";
@import "@/styles/admin/admin-card.css";
@import "@/styles/admin/admin-badge.css";

.content-wrapper {
  min-height: 0;
}

.left-section {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.table-section {
  flex: 1;
  overflow: auto;
}

.logger-table {
  width: 100%;
  min-width: 0;
  table-layout: auto;
}

/* 공통 data-table의 ... 처리 강제 해제 */
.logger-table th,
.logger-table td {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: keep-all;
  overflow-wrap: anywhere;
  line-height: 1.45;
}

/* 테이블 행 hover active 효과 */
.logger-table tbody tr {
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.logger-table tbody tr:hover td {
  background: #f8fafc;
}

.logger-table tbody tr:hover {
  box-shadow: inset 3px 0 0 #e58a13;
}

.logger-table tbody tr.row-selected td {
  background: #ffedd5;
}

.logger-table tbody tr.row-selected {
  box-shadow: inset 3px 0 0 #e58a13;
}

.col-logger {
  width: 210px;
}

.col-line {
  width: 120px;
}

.col-site {
  width: auto;
}

.col-type {
  width: 120px;
}

.col-node {
  width: 70px;
}

.col-status {
  width: 120px;
}

.col-actions {
  width: 100px;
}

.col-logger-cell,
.col-site-cell {
  text-align: left;
  padding: 10px 14px;
}

.col-line-cell,
.col-type-cell,
.col-node-cell,
.col-status-cell {
  text-align: center;
  white-space: nowrap;
}

.row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
}

.mini-warning-text {
  margin-top: 4px;
  font-size: 11px;
  color: #d97706;
}

.mode-text {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
}

.mode-text.create {
  color: #2563eb;
}

.mode-text.edit {
  color: #d97706;
}

.mt-12 {
  margin-top: 12px;
}

.mt-16 {
  margin-top: 16px;
}

.mt-20 {
  margin-top: 20px;
}

/* =========================
   중앙 대형 모달
========================= */

.multi-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background: rgba(15, 23, 42, 0.48);
}

.multi-modal-panel {
  width: min(1180px, 94vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
}

.multi-modal-header {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 26px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}

.multi-modal-title {
  font-size: 20px;
  font-weight: 800;
  color: #111827;
}

.multi-modal-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-close-btn {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  text-align: center;
}

.modal-close-btn:hover {
  background: #e5e7eb;
}

.multi-modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 26px 28px;
  background: #f8fafc;
}

.modal-two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.modal-section {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 20px;
}

.section-title {
  font-size: 15px;
  font-weight: 800;
  color: #111827;
}

.group-form-grid {
  grid-template-columns: 1fr;
}

.preview-form-row,
.action-row {
  grid-column: 1 / -1;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.form-row {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.form-row.align-start {
  align-items: flex-start;
}

.form-label {
  width: auto;
  min-width: 0;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  white-space: nowrap;
}

.form-label.required::after {
  content: " *";
  color: #ef4444;
}

.form-field {
  min-width: 0;
}

.control {
  width: 100%;
  min-width: 0;
}

.warning-box {
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 13px;
  line-height: 1.5;
}

.preview-box {
  width: 100%;
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}

.preview-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid #edf0f4;
  font-size: 12px;
}

.preview-row:last-child {
  border-bottom: none;
}

.preview-col {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-button-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.node-table-scroll {
  max-height: 260px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
}

.node-table {
  min-width: 820px;
}

/* 노드 후보 목록 hover active 효과 */
.node-table tbody tr {
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.node-table tbody tr:hover td {
  background: #f8fafc;
}

.node-table tbody tr:hover {
  box-shadow: inset 3px 0 0 #e58a13;
}

.empty-text,
.modal-empty {
  padding: 28px;
  color: #8a94a6;
  text-align: center;
  font-size: 14px;
}

@media (max-width: 1200px) {
  .modal-two-column {
    grid-template-columns: 1fr;
  }

  .form-grid,
  .group-form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .multi-modal-backdrop {
    padding: 12px;
  }

  .multi-modal-panel {
    width: 100%;
    max-height: 94vh;
  }

  .multi-modal-header {
    padding: 18px;
  }

  .multi-modal-body {
    padding: 18px;
  }

  .form-row {
    grid-template-columns: 1fr;
    align-items: stretch;
    gap: 6px;
  }

  .form-label {
    width: auto;
  }
}

.filter-grid {
  display: flex;
  gap: 14px;
  align-items: flex-end;
  width: 100%;
}

.filter-item {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-item:last-of-type {
  max-width: 240px;
}

.filter-actions {
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.filter-actions .btn {
  width: 110px;
  height: 42px;
  white-space: nowrap;
}

@media (max-width: 1400px) {
  .filter-grid {
    flex-wrap: wrap;
  }

  .filter-item {
    min-width: 240px;
  }

  .filter-actions {
    margin-left: auto;
  }
}

@media (max-width: 900px) {
  .filter-grid {
    flex-direction: column;
  }

  .filter-item,
  .filter-item:last-of-type {
    width: 100%;
    max-width: none;
  }

  .filter-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

.clickable-row {
  cursor: pointer;
}
</style>